
import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AgentService } from "@/services/agent-service";
import { Agent } from "@/models/agent";
import AgentGrid from "@/components/browse/AgentGrid";
import LoadingAgents from "@/components/browse/LoadingAgents";
import BrowseHeader from "@/components/browse/BrowseHeader";
import TagsList from "@/components/category/TagsList";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Tag, X } from "lucide-react";

const CategoryView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Decode and normalize the category from the URL parameter
  const decodedCategory = slug ? decodeURIComponent(slug) : "";
  const tag = searchParams.get("tag");

  // Load agents
  useEffect(() => {
    const loadAgents = async () => {
      setLoading(true);
      try {
        const data = await AgentService.getAgents();
        setAgents(data);
      } catch (error) {
        console.error("Failed to load agents:", error);
      } finally {
        setLoading(false);
      }
    };

    loadAgents();
  }, [slug, tag]);

  // Filter agents by category and optionally by tag
  const filteredAgents = agents.filter(agent => {
    const matchesSearch = searchQuery === "" || 
      agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.description.toLowerCase().includes(searchQuery.toLowerCase());
      
    const matchesCategory = !slug || agent.category.toLowerCase() === decodedCategory.toLowerCase();
    
    const matchesTag = !tag || agent.tags.some(t => t.toLowerCase() === tag.toLowerCase());
    
    return matchesSearch && matchesCategory && matchesTag;
  });

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Clear tag filter
  const clearTagFilter = () => {
    setSearchParams(params => {
      params.delete("tag");
      return params;
    });
  };

  // Capitalize first letter for display
  const displayCategory = decodedCategory.charAt(0).toUpperCase() + decodedCategory.slice(1);

  return (
    <div className="container mx-auto px-4 py-8 min-h-screen bg-white dark:bg-gray-900/10">
      <Helmet>
        <title>{displayCategory || "Category"} Agents - OGMAI</title>
      </Helmet>
      
      <div className="mb-6">
        <Button variant="ghost" size="sm" asChild className="text-gray-600 hover:text-gray-900 mb-4">
          <Link to="/categories" className="flex items-center">
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back to Categories
          </Link>
        </Button>
        
        <BrowseHeader searchQuery={searchQuery} onSearch={handleSearch} />
      </div>
      
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">{displayCategory}</h1>
          {tag && (
            <Badge variant="secondary" className="flex items-center gap-1 ml-2 bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200">
              <Tag className="h-3 w-3" />
              {tag}
              <X 
                className="h-3 w-3 ml-1 cursor-pointer" 
                onClick={clearTagFilter}
              />
            </Badge>
          )}
        </div>
        
        <p className="text-gray-600 dark:text-gray-300">
          Browse AI agents in the {displayCategory} category
          {tag ? ` tagged with "${tag}"` : ""}.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-1 bg-gray-50 dark:bg-gray-800/20 p-4 rounded-lg shadow-sm">
          {!loading && <TagsList agents={agents} category={displayCategory} selectedTag={tag} />}
        </div>
        
        <div className="lg:col-span-3">
          {loading ? (
            <LoadingAgents />
          ) : (
            <>
              <div className="flex justify-between items-center mb-6 px-4 py-3 bg-gray-50 dark:bg-gray-800/20 rounded-lg">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {filteredAgents.length} {filteredAgents.length === 1 ? "agent" : "agents"} found
                </div>
              </div>
              
              {filteredAgents.length > 0 ? (
                <AgentGrid agents={filteredAgents} />
              ) : (
                <div className="text-center py-16 bg-gray-50 dark:bg-gray-800/10 rounded-lg">
                  <h3 className="text-xl font-medium mb-2 text-gray-800 dark:text-gray-200">No agents found</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-8">
                    There are no agents in this category{tag ? " with the selected tag" : ""}.
                  </p>
                  <Button asChild variant="outline" className="bg-white dark:bg-gray-800">
                    <Link to="/browse">Browse All Agents</Link>
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoryView;
